/**
 * OverviewScreen — scope-parameterised overview for State, DISCOM, and Vendor roles.
 * Props:
 *   scope: 'state' | 'discom' | 'vendor'
 */
import React from 'react';
import KPICard from '../../components/shared/KPICard';
import StatusPill from '../../components/shared/StatusPill';
import SectionDivider from '../../components/shared/SectionDivider';
import {
  gujaratStateKPIs,
  gujaratDistricts,
  pgvclOverview,
  feeders,
  vendorKPIs,
} from '../../data/mockData';

type Scope = 'state' | 'discom' | 'vendor';

interface OverviewScreenProps {
  scope: Scope;
}

/* ── Gujarat District Map Placeholder ────────────────────────────────────── */
function GujaratMapPanel() {
  const tiles = [
    { name: 'Kutch',       shade: '#BFDBFE' },
    { name: 'Mehsana',     shade: '#93C5FD' },
    { name: 'Gandhinagar', shade: '#3B82F6' },
    { name: 'Rajkot',      shade: '#93C5FD' },
    { name: 'Ahmedabad',   shade: '#1E40AF' },
    { name: 'Anand',       shade: '#3B82F6' },
    { name: 'Junagadh',    shade: '#BFDBFE' },
    { name: 'Bhavnagar',   shade: '#DBEAFE' },
    { name: 'Vadodara',    shade: '#60A5FA' },
    { name: 'Surat',       shade: '#2472C8' },
  ];

  return (
    <div className="h-full flex flex-col items-center justify-center bg-bg-secondary rounded-input border border-border p-4">
      <p className="text-xs text-text-tertiary mb-3">Gujarat Districts — Generation Intensity</p>
      <div className="grid grid-cols-3 gap-2 w-full max-w-xs">
        {tiles.map(d => (
          <div
            key={d.name}
            className="rounded-input p-2 text-center cursor-pointer border border-white hover:border-accent transition-colors"
            style={{ backgroundColor: d.shade }}
          >
            <p className="text-white font-medium leading-tight" style={{ fontSize: 10, textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}>
              {d.name}
            </p>
          </div>
        ))}
      </div>
      <p className="text-xs text-text-tertiary mt-3">Click district to drill down</p>
    </div>
  );
}

/* ── DISCOM Feeder Map Placeholder ──────────────────────────────────────── */
function FeederMapPanel() {
  return (
    <div className="h-full flex flex-col items-center justify-center bg-bg-secondary rounded-input border border-border p-4">
      <p className="text-xs text-text-tertiary mb-3">PGVCL Feeders — Solar Generation (MW)</p>
      <div className="grid grid-cols-3 gap-2 w-full">
        {feeders.map(f => (
          <div
            key={f.id}
            className="bg-accent/10 border border-accent/20 rounded-input p-2 text-center cursor-pointer hover:bg-accent/20 transition-colors"
          >
            <p className="text-accent font-medium" style={{ fontSize: 10 }}>{f.name.split(' ')[0]}</p>
            <p className="text-text-secondary mt-0.5" style={{ fontSize: 9 }}>{f.generationMW} MW</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-text-tertiary mt-3">GIS feeder map — future phase</p>
    </div>
  );
}

/* ── Vendor Portfolio by State ───────────────────────────────────────────── */
function VendorStateCards() {
  const states = [
    { state: 'Gujarat',     plants: 142, pct: 42 },
    { state: 'Tamil Nadu',  plants: 76,  pct: 22 },
    { state: 'Rajasthan',   plants: 58,  pct: 17 },
    { state: 'Maharashtra', plants: 42,  pct: 12 },
    { state: 'Karnataka',   plants: 24,  pct: 7  },
  ];

  return (
    <div className="grid grid-cols-5 gap-3">
      {states.map(s => (
        <div key={s.state} className="text-center bg-bg-secondary rounded-input p-3 border border-border/50">
          <p className="text-sm font-bold text-text-primary">{s.plants}</p>
          <p className="text-xs text-text-secondary mt-0.5">{s.state}</p>
          <p className="text-xs text-text-tertiary mt-0.5">{s.pct}%</p>
        </div>
      ))}
    </div>
  );
}

/* ── Vendor Commissioning Status ─────────────────────────────────────────── */
function CommissioningStatus() {
  const items = [
    { label: 'Commissioned', count: vendorKPIs.commissioned,  color: 'text-success',       bar: 'bg-success',   bgBar: 'bg-green-100' },
    { label: 'Pending',      count: vendorKPIs.pending,       color: 'text-warning',       bar: 'bg-warning',   bgBar: 'bg-orange-100' },
    { label: 'Under Test',   count: vendorKPIs.underTest,     color: 'text-accent',        bar: 'bg-accent',    bgBar: 'bg-blue-100' },
    { label: 'Inactive',     count: vendorKPIs.inactive,      color: 'text-text-tertiary', bar: 'bg-gray-400',  bgBar: 'bg-gray-100' },
  ];
  return (
    <div className="flex gap-4">
      {items.map(i => (
        <div key={i.label} className="bg-white rounded-card border border-border shadow-card p-4 flex-1 text-center">
          <p className={`text-2xl font-bold ${i.color}`}>{i.count}</p>
          <p className="text-xs text-text-secondary mt-1">{i.label}</p>
          <div className={`mt-2 ${i.bgBar} rounded-full h-1.5 overflow-hidden`}>
            <div className={`h-full ${i.bar} rounded-full`} style={{ width: `${(i.count / vendorKPIs.totalPlants) * 100}%` }} />
          </div>
          <p className="text-xs text-text-tertiary mt-1">{((i.count / vendorKPIs.totalPlants) * 100).toFixed(0)}%</p>
        </div>
      ))}
    </div>
  );
}

/* ── KPI rows per scope (standard 6-card layout) ─────────────────────────── */
function StateKPIs() {
  const offlineFault = gujaratStateKPIs.offlineFaultCount;
  const offlinePct = (100 - gujaratStateKPIs.activePercent).toFixed(0);
  return (
    <div className="flex gap-4">
      <KPICard title="Total Plants"           value={gujaratStateKPIs.plants.toLocaleString()} subtext="Across 33 districts" />
      <KPICard title="Installed Capacity"     value={`${gujaratStateKPIs.capacityGWp} GWp`} subtext="Installed" />
      <KPICard title="Today's Generation"     value={`${gujaratStateKPIs.generationGWh} GWh`} subtext="+3.2% vs yesterday" subtextColor="green" />
      <KPICard title="Active Plants"          value={`${gujaratStateKPIs.activePercent}%`} subtext="5,678 of 6,240" subtextColor="green" />
      <KPICard title="Offline / Fault"        value={offlineFault.toLocaleString()} subtext={`${offlinePct}% needs review`} subtextColor="red" bgColor="bg-red-50" />
      <KPICard title="Avg Performance Ratio"  value={`${gujaratStateKPIs.avgPR}%`} subtext="Good zone (>72%)" subtextColor="green" />
    </div>
  );
}

function DiscomKPIs() {
  return (
    <div className="flex gap-4">
      <KPICard title="Total Plants"           value={pgvclOverview.totalPlants.toLocaleString()} subtext="PGVCL area" />
      <KPICard title="Installed Capacity"     value={`${pgvclOverview.capacityMWp} MWp`} subtext="Installed" />
      <KPICard title="Today's Generation"     value={`${pgvclOverview.todayMWh} MWh`} subtext="+2.8% vs yesterday" subtextColor="green" />
      <KPICard title="Active Plants"          value={`${pgvclOverview.activePct}%`} subtext={`${pgvclOverview.activePlants} of ${pgvclOverview.totalPlants}`} subtextColor="green" />
      <KPICard title="Offline / Fault"        value={pgvclOverview.offlinePlants} subtext="Needs attention" subtextColor="red" bgColor="bg-red-50" />
      <KPICard title="Avg Performance Ratio"  value={`${pgvclOverview.avgPR}%`} subtext="PGVCL average" subtextColor="green" />
    </div>
  );
}

function VendorKPIs() {
  return (
    <div className="flex gap-4">
      <KPICard title="Total Plants"       value={vendorKPIs.totalPlants.toLocaleString()} subtext="Zebron Solar portfolio" />
      <KPICard title="Installed Capacity" value={`${vendorKPIs.totalCapacityMWp} MWp`} subtext="Installed" />
      <KPICard title="Today's Generation" value={`${vendorKPIs.todayMWh} MWh`} subtext="+2.1% vs yesterday" subtextColor="green" />
      <KPICard title="Active Plants"      value={vendorKPIs.activePlants} subtext={`${((vendorKPIs.activePlants / vendorKPIs.totalPlants) * 100).toFixed(0)}% of portfolio`} subtextColor="green" />
      <KPICard title="Offline / Fault"    value={vendorKPIs.offlinePlants} subtext="Needs attention" subtextColor="red" bgColor="bg-red-50" />
      <KPICard title="Low PR (&lt;70%)"   value={vendorKPIs.lowPRPlants} bgColor="bg-orange-50" subtextColor="amber" subtext="Underperformers" />
    </div>
  );
}

/* ── Secondary KPI rows per scope ────────────────────────────────────────── */
function StateSecondaryKPIs() {
  return (
    <div className="flex gap-4">
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">Avg CUF Today</p>
        <p className="text-xl font-bold text-text-primary mt-1">{gujaratStateKPIs.avgCufPct}%</p>
        <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
      </div>
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">CO₂ Offset Lifetime</p>
        <p className="text-xl font-bold text-teal mt-1">{gujaratStateKPIs.co2OffsetMT} MT</p>
        <p className="text-xs text-text-secondary mt-1">Est. × 0.71 kg/kWh</p>
      </div>
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">State Avg PR</p>
        <p className="text-xl font-bold text-success mt-1">{gujaratStateKPIs.avgPR}%</p>
        <p className="text-xs text-success mt-1">Good zone (&gt;72%)</p>
      </div>
    </div>
  );
}

function DiscomSecondaryKPIs() {
  const isLive = pgvclOverview.sldcFeedStatus === 'LIVE';
  return (
    <div className="flex gap-4">
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">Avg CUF Today</p>
        <p className="text-xl font-bold text-text-primary mt-1">{pgvclOverview.avgCufPct}%</p>
        <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
      </div>
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">CO₂ Offset Lifetime</p>
        <p className="text-xl font-bold text-teal mt-1">{pgvclOverview.discomCo2OffsetMT} MT</p>
        <p className="text-xs text-text-secondary mt-1">Est. × 0.71 kg/kWh</p>
      </div>
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">SLDC Feed Status</p>
        <div className="mt-1">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-sm font-bold ${isLive ? 'bg-green-100 text-success' : 'bg-red-100 text-danger'}`}>
            <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-success' : 'bg-danger'}`} />
            {pgvclOverview.sldcFeedStatus}
          </span>
        </div>
        <p className="text-xs text-text-secondary mt-1">Real-time sync active</p>
      </div>
    </div>
  );
}

function VendorSecondaryKPIs() {
  return (
    <div className="flex gap-4">
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">Avg CUF Today</p>
        <p className="text-xl font-bold text-text-primary mt-1">{vendorKPIs.avgCufPct}%</p>
        <p className="text-xs text-text-secondary mt-1">Peak 10am–3pm</p>
      </div>
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">CO₂ Offset Lifetime</p>
        <p className="text-xl font-bold text-teal mt-1">{vendorKPIs.co2OffsetMT} MT</p>
        <p className="text-xs text-text-secondary mt-1">Est. × 0.71 kg/kWh</p>
      </div>
      <div className="bg-white rounded-card border border-border shadow-card p-4 flex-1">
        <p className="text-xs text-text-tertiary font-medium uppercase tracking-wide">Avg Performance Ratio</p>
        <p className="text-xl font-bold text-success mt-1">{vendorKPIs.avgPrPct}%</p>
        <p className="text-xs text-success mt-1">Portfolio average</p>
      </div>
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
export default function OverviewScreen({ scope }: OverviewScreenProps) {
  return (
    <div className="p-6 space-y-6">
      {/* KPI row */}
      {scope === 'state'  && <StateKPIs />}
      {scope === 'discom' && <DiscomKPIs />}
      {scope === 'vendor' && <VendorKPIs />}

      {/* Map / visual panel + summary table (state and discom) */}
      {(scope === 'state' || scope === 'discom') && (
        <div className="flex gap-4">
          <div className="w-[55%] bg-white rounded-card border border-border shadow-card p-4">
            <SectionDivider
              title={scope === 'state' ? 'Gujarat District Map' : 'Feeder Map — PGVCL'}
              subtitle={scope === 'state' ? 'Generation heatmap' : 'Active feeders by circle'}
            />
            <div style={{ height: 320 }}>
              {scope === 'state' ? <GujaratMapPanel /> : <FeederMapPanel />}
            </div>
          </div>

          <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
            {scope === 'state' && (
              <>
                <SectionDivider title="District Summary" subtitle="Today" />
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-text-tertiary font-medium">District</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">Plants</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">GWh</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">PR%</th>
                      <th className="text-center py-2 text-text-tertiary font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gujaratDistricts.map(d => (
                      <tr key={d.id} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                        <td className="py-2.5 font-medium text-text-primary">{d.name}</td>
                        <td className="py-2.5 text-right text-text-secondary">{d.plants.toLocaleString()}</td>
                        <td className="py-2.5 text-right font-medium text-text-primary">{d.generationGWh.toFixed(2)}</td>
                        <td className={`py-2.5 text-right font-semibold ${d.prPercent >= 75 ? 'text-success' : 'text-warning'}`}>{d.prPercent.toFixed(1)}%</td>
                        <td className="py-2.5 text-center"><StatusPill status={d.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {scope === 'discom' && (
              <>
                <SectionDivider title="Feeder Summary" subtitle="PGVCL active feeders" />
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-text-tertiary font-medium">Feeder</th>
                      <th className="text-left py-2 text-text-tertiary font-medium">Circle</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">MW</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">Plants</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">Penetration</th>
                      <th className="text-right py-2 text-text-tertiary font-medium">Alarms</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feeders.map(f => (
                      <tr key={f.id} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                        <td className="py-2.5">
                          <p className="font-medium text-text-primary">{f.name}</p>
                          <p className="text-text-tertiary" style={{ fontSize: 10 }}>{f.id}</p>
                        </td>
                        <td className="py-2.5 text-text-secondary">{f.circle}</td>
                        <td className="py-2.5 text-right font-medium text-text-primary">{f.generationMW}</td>
                        <td className="py-2.5 text-right text-text-secondary">{f.plants}</td>
                        <td className="py-2.5 text-right text-text-secondary">{f.penetrationPercent}%</td>
                        <td className={`py-2.5 text-right ${f.alarms > 0 ? 'text-warning font-semibold' : 'text-text-secondary'}`}>{f.alarms}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Vendor: commissioning + state breakdown */}
      {scope === 'vendor' && (
        <>
          <div>
            <SectionDivider title="Commissioning Status" subtitle="Zebron Solar portfolio breakdown" />
            <CommissioningStatus />
          </div>
          <div className="bg-white rounded-card border border-border shadow-card p-4">
            <SectionDivider title="Portfolio by State" />
            <VendorStateCards />
          </div>
        </>
      )}

      {/* Secondary KPI row — all three scopes */}
      {scope === 'state'  && <StateSecondaryKPIs />}
      {scope === 'discom' && <DiscomSecondaryKPIs />}
      {scope === 'vendor' && <VendorSecondaryKPIs />}
    </div>
  );
}
